import React, { useEffect, useRef, useState } from 'react';
import SignaturePad from 'react-signature-canvas';
import { Button, Typography } from '@mui/material';

interface SignatureSectionProps {
  signatureDataUrl: string | undefined;
  signaturePadRef: React.RefObject<SignaturePad>;
  onSignatureChange: (hasChanged: boolean) => void;
}

const MAX_WIDTH = 500;
const ASPECT_RATIO = 2.5; // 500/200

const SignatureSection: React.FC<SignatureSectionProps> = ({
  signatureDataUrl,
  signaturePadRef,
  onSignatureChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasWidth, setCanvasWidth] = useState(MAX_WIDTH);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const width = Math.min(containerRef.current.offsetWidth, MAX_WIDTH);
        setCanvasWidth(width);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!signaturePadRef.current) return;

    if (!signatureDataUrl) {
      signaturePadRef.current.clear();
      onSignatureChange(false);
      return;
    }

    const img = new window.Image();
    img.onload = () => {
      if (!signaturePadRef.current) return;
      const canvas = signaturePadRef.current.getCanvas();
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      }
      onSignatureChange(false);
    };
    img.src = signatureDataUrl;
  }, [signatureDataUrl, signaturePadRef, onSignatureChange, canvasWidth]);

  const handleClearSignature = () => {
    signaturePadRef.current?.clear();
    onSignatureChange(true);
  };

  const canvasHeight = canvasWidth / ASPECT_RATIO;

  return (
    <>
      <Typography variant="h6">Firma</Typography>
      <div
        ref={containerRef}
        style={{
          border: '1px solid #000',
          width: '100%',
          maxWidth: MAX_WIDTH,
          margin: '0 auto',
        }}
      >
        <SignaturePad
          ref={signaturePadRef}
          minWidth={0.75}
          maxWidth={1.5}
          penColor="#000"
          onBegin={() => onSignatureChange(true)}
          canvasProps={{
            width: canvasWidth,
            height: canvasHeight,
            style: {
              width: `${canvasWidth}px`,
              height: `${canvasHeight}px`,
              display: 'block',
            },
          }}
        />
      </div>
      <Button onClick={handleClearSignature}>Limpiar firma</Button>
    </>
  );
};

export default SignatureSection;
